import type { Character } from '@dcc/rules/domain/character.ts';
import type { Effect } from '@dcc/rules/rules/effects.ts';
import type { EditDraft } from '@dcc/rules/app/characterEditService.ts';
import { changesFromDraft } from '@dcc/rules/app/characterEditService.ts';
import { applyChanges } from '@dcc/rules/rules/characterChanges.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { computeSheet } from './characterService.ts';

export type SheetEditResult =
  | { ok: true; character: Character }
  | { ok: false; problems: string[] };

/**
 * Applies the player's Edit Sheet draft through the same audited CharacterChange
 * authority used by creation/advancement/manual editing.
 *
 * The GM-controlled Floor is deliberately copied from the CURRENT saved
 * character instead of trusting the draft. If the GM changes floors while a
 * player has the editor open, saving the editor can never write the old floor
 * back into the character or its audit log.
 *
 * Lowering Intelligence can lower Max Mana. Current Mana is therefore clamped
 * in the SAME atomic change batch when needed; raising Intelligence does not
 * grant free Mana.
 */
export function applySheetEdit(
  character: Character,
  draft: EditDraft,
  effects: readonly Effect[] = [],
  inCombat = false,
): SheetEditResult {
  const ids = ['str', 'int', 'con', 'dex', 'cha'] as const;
  const statsChanged = ids.some(id => draft.stats[id] !== character.unenhancedStats[id]);
  if (statsChanged) {
    if (inCombat) return { ok: false, problems: ['Attribute redistribution is only allowed outside combat.'] };
    if (draft.level !== character.level) return { ok: false, problems: ['Save level changes separately from attribute redistribution.'] };
    const total = ids.reduce((sum, id) => sum + draft.stats[id], 0);
    const original = ids.reduce((sum, id) => sum + character.unenhancedStats[id], 0);
    if (total !== original) return { ok: false, problems: [`Keep the same total attribute points (${original}). Current total: ${total}. Use Advancement to spend earned Stat Points.`] };
  }
  const authoritativeDraft: EditDraft = {
    ...draft,
    floor: character.floor,
    stats: { ...draft.stats },
    skills: draft.skills.map((entry) => ({ ...entry })),
  };

  const changes = changesFromDraft(character, authoritativeDraft);
  if (changes.length === 0) return { ok: true, character };

  const requests = changes.map((change) => ({
    change,
    source: 'manual-edit' as const,
    reason: 'Edit Sheet',
  }));
  const options = {
    now: new Date().toISOString(),
    lookupSkill: (skillId: string) => contentRegistry.getSkill(skillId),
  };

  let result = applyChanges(character, requests, options);
  if (!result.ok) {
    return { ok: false, problems: result.problems.map((problem) => problem.message) };
  }

  const maxMana = computeSheet(result.character, effects).derived.mana.max;
  if (result.character.currentMana > maxMana) {
    result = applyChanges(
      character,
      [
        ...requests,
        {
          change: { kind: 'set-current-mana' as const, currentMana: maxMana },
          source: 'manual-edit' as const,
          reason: 'Edit Sheet reduced Max Mana',
        },
      ],
      options,
    );
    if (!result.ok) {
      return { ok: false, problems: result.problems.map((problem) => problem.message) };
    }
  }

  return { ok: true, character: result.character };
}
